// Payroll History Service
// Persists payroll snapshots to Firestore and retrieves historical records
// Collection: payrollRecords

import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { scopedQuery, ensureActiveShopId } from './tenantQuery';
import type { PayrollReportData, BarberPayrollBreakdown } from '../types/reporting';

// Firestore document shape for a payroll snapshot
export interface PayrollRecord {
  id: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  barberPayroll: BarberPayrollBreakdown[];
  totalRevenue: number;
  totalCommission: number;
  shopProfit: number;
  totalTips: number;
  frequency: string;
  createdAt: Timestamp;
}

const COLLECTION = 'payrollRecords';

/**
 * Build a deterministic doc ID from the pay period dates.
 * Using the date range ensures re-generating the same period overwrites
 * rather than creating duplicates.
 */
// Doc id is shop-namespaced so two shops' same-week snapshots can't collide. Legacy ids were
// `${start}_${end}` (single-shop era) — savePayrollSnapshot deletes that legacy doc on save so the
// same period can't duplicate. BEHAVIOR-PIN: multi-tenant Step 5.
function buildDocId(shopId: string, startDate: string, endDate: string): string {
  return `${shopId}_${startDate}_${endDate}`;
}
function legacyDocId(startDate: string, endDate: string): string {
  return `${startDate}_${endDate}`;
}

/**
 * Save a payroll snapshot to Firestore.
 * Called automatically after payroll is calculated.
 */
export async function savePayrollSnapshot(
  payPeriodStart: string,
  payPeriodEnd: string,
  data: PayrollReportData
): Promise<void> {
  try {
    const shopId = (await ensureActiveShopId()) || 'unknown-shop';
    const docId = buildDocId(shopId, payPeriodStart, payPeriodEnd);
    const docRef = doc(db, COLLECTION, docId);

    const record: Omit<PayrollRecord, 'id'> = {
      shopId,
      payPeriodStart,
      payPeriodEnd,
      barberPayroll: data.barbers,
      totalRevenue: data.shopTotals.grossRevenue,
      totalCommission: data.shopTotals.totalCommissionsPaid,
      shopProfit: data.shopTotals.shopProfit,
      totalTips: data.shopTotals.totalTips,
      frequency: data.period.frequency,
      createdAt: Timestamp.now(),
    } as Omit<PayrollRecord, 'id'>;

    await setDoc(docRef, record);
    // Retire the legacy single-shop-era doc for this period so it can't show as a duplicate.
    const legacy = legacyDocId(payPeriodStart, payPeriodEnd);
    if (legacy !== docId) { try { await deleteDoc(doc(db, COLLECTION, legacy)); } catch { /* none — fine */ } }
    console.log(`💾 Payroll snapshot saved for ${payPeriodStart} — ${payPeriodEnd}`);
  } catch (error) {
    console.error('Error saving payroll snapshot:', error);
    // Non-blocking — calculation still returns even if save fails
  }
}

/**
 * Retrieve payroll history from Firestore.
 * Optional date filters narrow the results; omitting both returns all records.
 */
export async function getPayrollHistory(
  startDate?: string,
  endDate?: string
): Promise<PayrollRecord[]> {
  try {
    const colRef = collection(db, COLLECTION);

    // Build query constraints based on provided filters
    const constraints: ReturnType<typeof where>[] = [];

    if (startDate) {
      constraints.push(where('payPeriodStart', '>=', startDate));
    }
    if (endDate) {
      constraints.push(where('payPeriodStart', '<=', endDate));
    }

    await ensureActiveShopId();
    const q = scopedQuery(colRef, ...constraints, orderBy('payPeriodStart', 'desc'));
    const snapshot = await getDocs(q);

    const records: PayrollRecord[] = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as PayrollRecord[];

    console.log(`📊 Retrieved ${records.length} payroll record(s)`);
    return records;
  } catch (error) {
    console.error('Error retrieving payroll history:', error);
    return [];
  }
}
